/**
 * Terms of Service Page
 * Complete terms and conditions for TRONA platform
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Text, Link } from '@chakra-ui/react';
import { MdGavel } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import LegalPageLayout from '../../components/legal/LegalPageLayout';
import {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalTable,
  LegalSubSection,
  LegalContact,
  LegalDivider,
} from '../../components/legal/LegalSection';
import { LEGAL_CONFIG } from '../../config/legal';

const TermsPage = () => {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t('terms.title', 'Términos de Servicio')}
      subtitle={t(
        'terms.subtitle',
        'Por favor lea estos términos cuidadosamente antes de usar nuestros servicios'
      )}
      documentType="terms"
      icon={MdGavel}
    >
      {/* Introduction */}
      <LegalParagraph>
        {t(
          'terms.intro',
          `Bienvenido a ${LEGAL_CONFIG.company.name}. Estos Términos de Servicio ("Términos") rigen su uso de nuestra plataforma y servicios de formación de empresas. Al acceder o utilizar nuestros servicios, usted acepta estar sujeto a estos Términos.`
        )}
      </LegalParagraph>

      {/* Section 1: Definitions */}
      <LegalSection
        number={1}
        title={t('terms.definitions.title', 'Definiciones')}
        id="definitions"
      >
        <LegalParagraph>
          {t(
            'terms.definitions.intro',
            'En estos Términos, las siguientes palabras tienen los significados indicados:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'terms.definitions.platform',
              '"Plataforma" significa el sitio web tronallc.com y todas las aplicaciones asociadas.'
            ),
            t(
              'terms.definitions.services',
              '"Servicios" significa los servicios de formacion de LLC, registro de agente, obtencion de EIN, y servicios relacionados.'
            ),
            t(
              'terms.definitions.user',
              '"Usuario", "usted" o "su" significa cualquier persona que accede o utiliza la Plataforma.'
            ),
            t(
              'terms.definitions.company',
              '"Nosotros", "nos" o "nuestro" significa TRONA LLC, una empresa de Wyoming.'
            ),
            t(
              'terms.definitions.content',
              '"Contenido" significa toda la información, datos, textos y materiales en la Plataforma.'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 2: Services */}
      <LegalSection
        number={2}
        title={t('terms.services.title', 'Descripción de Servicios')}
        id="services"
      >
        <LegalParagraph>
          {t(
            'terms.services.description',
            'TRONA proporciona una plataforma para ayudar a emprendedores a formar Limited Liability Companies (LLCs) en Estados Unidos. Nuestros servicios incluyen:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'terms.services.formation',
              'Preparación y presentación de documentos de formación de LLC'
            ),
            t('terms.services.agent', 'Servicios de Agente Registrado'),
            t(
              'terms.services.ein',
              'Obtención de EIN (Número de Identificación del Empleador)'
            ),
            t(
              'terms.services.documents',
              'Preparación de documentos corporativos (Operating Agreement, etc.)'
            ),
            t(
              'terms.services.banking',
              'Facilitación de apertura de cuentas bancarias empresariales'
            ),
          ]}
        />
        <LegalDisclaimer
          type="info"
          title={t('terms.services.disclaimer.title', 'Nota Importante')}
        >
          {t(
            'terms.services.disclaimer.text',
            'No somos un despacho de abogados y no proporcionamos asesoramiento legal. Para asuntos legales complejos, recomendamos consultar con un abogado licenciado.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 3: Plans and Pricing */}
      <LegalSection
        number={3}
        title={t('terms.pricing.title', 'Planes y Precios')}
        id="pricing"
      >
        <LegalParagraph>
          {t(
            'terms.pricing.description',
            'Ofrecemos los siguientes planes de formacion:'
          )}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('terms.pricing.plan', 'Plan'),
            t('terms.pricing.price', 'Precio'),
            t('terms.pricing.includes', 'Incluye'),
          ]}
          rows={[
            [
              LEGAL_CONFIG.plans.business.name,
              `$${LEGAL_CONFIG.plans.business.price} USD`,
              t(
                'terms.pricing.business.includes',
                'LLC, EIN, documentos base, soporte bancario y preparacion de cobros'
              ),
            ],
            [
              LEGAL_CONFIG.plans.custom.name,
              t('terms.pricing.custom.price', 'A medida'),
              t(
                'terms.pricing.custom.includes',
                'Plan contact-only para estructuras complejas o necesidades especiales'
              ),
            ],
          ]}
        />
        <LegalParagraph>
          {t(
            'terms.pricing.fees',
            'Los precios no incluyen tarifas gubernamentales estatales, que varian segun el estado seleccionado. Las tarifas de Wyoming son aproximadamente $100 USD.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 4: User Obligations */}
      <LegalSection
        number={4}
        title={t('terms.obligations.title', 'Obligaciones del Usuario')}
        id="obligations"
      >
        <LegalParagraph>
          {t(
            'terms.obligations.intro',
            'Al utilizar nuestros servicios, usted acepta:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'terms.obligations.accurate',
              'Proporcionar información precisa y veraz'
            ),
            t(
              'terms.obligations.legal',
              'Usar los servicios únicamente para propósitos legales'
            ),
            t(
              'terms.obligations.compliance',
              'Cumplir con todas las leyes y regulaciones aplicables'
            ),
            t(
              'terms.obligations.account',
              'Mantener la confidencialidad de su cuenta'
            ),
            t(
              'terms.obligations.update',
              'Actualizar su información cuando cambie'
            ),
            t(
              'terms.obligations.aup',
              'Cumplir con nuestra Política de Uso Aceptable'
            ),
          ]}
        />
        <LegalParagraph>
          {t(
            'terms.obligations.aup.link',
            'Para más detalles, consulte nuestra '
          )}
          <Link as={RouterLink} to="/acceptable-use" color="brand.500">
            {t('terms.obligations.aup.linkText', 'Política de Uso Aceptable')}
          </Link>
          .
        </LegalParagraph>
      </LegalSection>

      {/* Section 5: Payment Terms */}
      <LegalSection
        number={5}
        title={t('terms.payment.title', 'Términos de Pago')}
        id="payment"
      >
        <LegalParagraph>
          {t(
            'terms.payment.description',
            'Todos los pagos se procesan a traves de Stripe, un procesador de pagos seguro. Al realizar un pago, usted acepta:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'terms.payment.authorize',
              'Autorizar el cargo a su método de pago'
            ),
            t(
              'terms.payment.currency',
              'Los precios están en Dólares Estadounidenses (USD)'
            ),
            t(
              'terms.payment.nonrefundable',
              'Las tarifas gubernamentales no son reembolsables'
            ),
            t(
              'terms.payment.refund',
              'Las solicitudes de reembolso estan sujetas a nuestra politica de reembolsos'
            ),
          ]}
        />
        <LegalParagraph>
          {t(
            'terms.payment.refund.link',
            'Para detalles sobre reembolsos, consulte nuestra '
          )}
          <Link as={RouterLink} to="/refund-policy" color="brand.500">
            {t('terms.payment.refund.linkText', 'Política de Reembolsos')}
          </Link>
          .
        </LegalParagraph>
      </LegalSection>

      {/* Section 6: Intellectual Property */}
      <LegalSection
        number={6}
        title={t('terms.ip.title', 'Propiedad Intelectual')}
        id="ip"
      >
        <LegalParagraph>
          {t(
            'terms.ip.ownership',
            `Todo el contenido de la Plataforma, incluyendo pero no limitado a textos, graficos, logos, iconos, imagenes, clips de audio, descargas digitales y compilaciones de datos, es propiedad de ${LEGAL_CONFIG.company.name} o sus proveedores de contenido y esta protegido por las leyes de propiedad intelectual de Estados Unidos e internacionales.`
          )}
        </LegalParagraph>
        <LegalParagraph>
          {t(
            'terms.ip.license',
            'Se le otorga una licencia limitada, no exclusiva y no transferible para acceder y utilizar la Plataforma para uso personal y comercial legitimo relacionado con nuestros servicios.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 7: Limitation of Liability */}
      <LegalSection
        number={7}
        title={t('terms.liability.title', 'Limitacion de Responsabilidad')}
        id="liability"
      >
        <LegalDisclaimer
          type="warning"
          title={t('terms.liability.important', 'Importante')}
        >
          <Text>
            {t(
              'terms.liability.disclaimer',
              `EN LA MAXIMA MEDIDA PERMITIDA POR LA LEY, ${LEGAL_CONFIG.company.name.toUpperCase()} NO SERA RESPONSABLE POR DANOS INDIRECTOS, INCIDENTALES, ESPECIALES, CONSECUENTES O PUNITIVOS, INCLUYENDO PERO NO LIMITADO A PERDIDA DE BENEFICIOS, DATOS, USO, BUENA VOLUNTAD U OTRAS PERDIDAS INTANGIBLES.`
            )}
          </Text>
        </LegalDisclaimer>
        <LegalParagraph>
          {t(
            'terms.liability.max',
            'Nuestra responsabilidad total no excedera el monto que usted pago por nuestros servicios en los doce (12) meses anteriores al evento que dio lugar a la reclamacion.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 8: Indemnification */}
      <LegalSection
        number={8}
        title={t('terms.indemnification.title', 'Indemnizacion')}
        id="indemnification"
      >
        <LegalParagraph>
          {t(
            'terms.indemnification.text',
            `Usted acepta indemnizar, defender y mantener indemne a ${LEGAL_CONFIG.company.name}, sus directores, empleados, agentes y afiliados de cualquier reclamación, responsabilidad, daño, pérdida y gasto, incluyendo honorarios razonables de abogados, que surjan de o estén relacionados con su uso de los servicios o violación de estos Términos.`
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 9: Dispute Resolution */}
      <LegalSection
        number={9}
        title={t('terms.disputes.title', 'Resolucion de Disputas')}
        id="disputes"
      >
        <LegalSubSection
          title={t('terms.disputes.arbitration.title', 'Arbitraje Vinculante')}
        >
          <LegalParagraph>
            {t(
              'terms.disputes.arbitration.text',
              `Cualquier disputa que surja de o esté relacionada con estos Términos o nuestros servicios será resuelta mediante arbitraje vinculante administrado por la ${LEGAL_CONFIG.jurisdiction.arbitration} de acuerdo con sus ${LEGAL_CONFIG.jurisdiction.arbitrationRules}.`
            )}
          </LegalParagraph>
        </LegalSubSection>
        <LegalSubSection
          title={t(
            'terms.disputes.classAction.title',
            'Renuncia a Demandas Colectivas'
          )}
        >
          <LegalParagraph>
            {t(
              'terms.disputes.classAction.text',
              'USTED ACEPTA QUE CUALQUIER RECLAMACION SERA PRESENTADA EN SU CAPACIDAD INDIVIDUAL Y NO COMO DEMANDANTE O MIEMBRO DE CLASE EN CUALQUIER SUPUESTO PROCEDIMIENTO DE CLASE O REPRESENTATIVO.'
            )}
          </LegalParagraph>
        </LegalSubSection>
      </LegalSection>

      {/* Section 10: Governing Law */}
      <LegalSection
        number={10}
        title={t('terms.law.title', 'Ley Aplicable')}
        id="governing-law"
      >
        <LegalParagraph>
          {t(
            'terms.law.text',
            `Estos Términos se regirán e interpretarán de acuerdo con las leyes del ${LEGAL_CONFIG.jurisdiction.governingLaw}, sin dar efecto a ningún principio de conflicto de leyes. Cualquier procedimiento legal que no esté sujeto a arbitraje se llevará a cabo exclusivamente en los tribunales ubicados en ${LEGAL_CONFIG.jurisdiction.venue}.`
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 11: Termination */}
      <LegalSection
        number={11}
        title={t('terms.termination.title', 'Terminacion')}
        id="termination"
      >
        <LegalParagraph>
          {t(
            'terms.termination.text',
            'Podemos suspender o terminar su acceso a nuestros servicios en cualquier momento, con o sin causa, con o sin previo aviso. Usted puede cancelar su cuenta en cualquier momento contactando a nuestro equipo de soporte.'
          )}
        </LegalParagraph>
        <LegalParagraph>
          {t(
            'terms.termination.survival',
            'Las disposiciones de estos Términos que por su naturaleza deban sobrevivir a la terminación, sobrevivirán, incluyendo pero no limitado a las secciones de propiedad intelectual, limitación de responsabilidad, indemnización y resolución de disputas.'
          )}
        </LegalParagraph>
      </LegalSection>

      {/* Section 12: Modifications */}
      <LegalSection
        number={12}
        title={t('terms.modifications.title', 'Modificaciones')}
        id="modifications"
      >
        <LegalParagraph>
          {t(
            'terms.modifications.text',
            'Nos reservamos el derecho de modificar estos Términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en la Plataforma. Su uso continuado de nuestros servicios después de cualquier modificación constituye su aceptación de los Términos modificados.'
          )}
        </LegalParagraph>
      </LegalSection>

      <LegalDivider />

      {/* Contact Information */}
      <LegalSection title={t('terms.contact.title', 'Contacto')} id="contact">
        <LegalParagraph>
          {t(
            'terms.contact.text',
            'Si tiene preguntas sobre estos Términos de Servicio, contáctenos:'
          )}
        </LegalParagraph>
        <LegalContact
          label={t('terms.contact.legal', 'Consultas Legales')}
          email={LEGAL_CONFIG.contact.legal}
          description={t(
            'terms.contact.legal.description',
            'Para preguntas sobre estos terminos'
          )}
        />
      </LegalSection>
    </LegalPageLayout>
  );
};

export default TermsPage;
